import { gtmPushData } from "@common/helpers/gtm";
import VehicleCardListSkeleton from "@common/components/loader/VehicleCardListSkeleton";
import useUser from "@modules/user/hooks/useUser";
import VehicleCard from "@modules/vehicle/components/VehicleCard";
import useVehicleList from "@modules/vehicle/hooks/useVehicleList";
import { UserFavouriteVehicle } from "@modules/vehicle/types/UserFavouriteVehicle";
import { FilterKeys } from "@modules/vehicle/types/vehicleFilter";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import React, { useContext, useEffect, useState } from "react";
import SaveMoreVehiclesCard from "@modules/vehicle/components/SaveMoreVehiclesCard";
import Button from "@common/components/Button";
import LoginModalContext from "@modules/user/LoginModalContext";
import EmptyVehicleCard from "@modules/vehicle/components/EmptyVehicleCard";
import LoadingSpinner from "@common/components/LoadingSpinner";
import InfiniteScroll from "react-infinite-scroll-component";
import getSaveMoreVehicleCardDetails from "@modules/vehicle/helpers/getSaveMoreVehicleCardDetails";
import Link from "next/link";
import { VLP_PATH } from "@common/helpers/isVehicleListingPage";
import BackButton from "../BackButton";
import EmptySavedVehicles from "./EmptySavedVehicles";

type Props = {
  onShowMenuClick?(): void;
};

const SavedVehicles = ({ onShowMenuClick }: Props) => {
  const { t } = useTranslation(["common", "profile"]);
  const { push, locale } = useRouter();
  const { user } = useUser();
  const [showEmptySavedVehicles, setShowEmptySavedVehicles] =
    useState<boolean>(true);
  const [favouriteVehiclesCopy, setFavouriteVehiclesCopy] =
    useState<UserFavouriteVehicle[]>();
  const { setShowModal: setShowLoginModal } = useContext(LoginModalContext);

  const isLoggedIn = user?.isLoggedIn;
  const LIMITED_SAVES_COUNT = 3;
  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useVehicleList({
    pageSize: isLoggedIn ? 300 : LIMITED_SAVES_COUNT,
    vehicleFilters: {
      filters: [{ key: FilterKeys.SavedVehicles, state: true }],
      isDirty: false,
    },
    locale: locale || "",
    favouriteVehicles: favouriteVehiclesCopy || [],
    enabled: true,
  });

  const isRightPaymentEligible =
    (data?.pages[0]?.hasPersonalTradeInValue ||
      data?.pages[0]?.hasPersonalInterestRate) ??
    false;

  const vehicles = data?.pages.flatMap((p) => p.vehicles) || [];

  useEffect(() => {
    if (user?.favouriteVehicles?.length) {
      setFavouriteVehiclesCopy(user?.favouriteVehicles);
    }
    if (user?.favouriteVehicles?.length === 0) {
      setShowEmptySavedVehicles(true);
    }
  }, [user?.favouriteVehicles]);

  useEffect(() => {
    if (
      !isLoading &&
      (favouriteVehiclesCopy === undefined ||
        favouriteVehiclesCopy?.length === 0)
    ) {
      setShowEmptySavedVehicles(true);
    } else {
      setShowEmptySavedVehicles(false);
    }
  }, [isLoading, favouriteVehiclesCopy]);

  const handleLoginClicked = () => {
    setShowLoginModal(true);
  };

  const navigateToVlp = () => {
    gtmPushData({
      event: "Profile",
      element: "CTA",
      descriptor: "Shop Inventory",
    });
    push(`/${VLP_PATH}`);
  };

  const getEmptyCards = () => {
    const savedFavouritesLength = favouriteVehiclesCopy?.length || 0;

    if (savedFavouritesLength > LIMITED_SAVES_COUNT) {
      return "";
    }

    return [1, 2, 3]
      .slice(savedFavouritesLength, LIMITED_SAVES_COUNT)
      .map((emptyCardIndex) => {
        return <EmptyVehicleCard index={emptyCardIndex} />;
      });
  };

  const saveMoreCardDetails = () => {
    return getSaveMoreVehicleCardDetails({
      isLoggedIn,
      favouriteVehiclesCopy,
      navigateToVlp,
      handleLoginClicked,
    });
  };

  const vehiclesList = () => {
    return (
      <>
        {isError && <div>Failed to load vehicles</div>}
        {isLoading &&
          (isLoggedIn ? (
            <VehicleCardListSkeleton
              numberOfCards={user?.favouriteVehicles?.length || 0}
            />
          ) : (
            <LoadingSpinner isLoading={isLoading} isOverlay={false} />
          ))}

        {!isLoading && favouriteVehiclesCopy !== undefined && (
          <div className="mt-6 grid grid-cols-1 gap-y-10 gap-x-6 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 3xl:grid-cols-5 xl:gap-x-8 grid-flow-row">
            {vehicles.map((vehicle, index) => {
              return (
                <React.Fragment key={vehicle.id}>
                  <VehicleCard
                    isRightPaymentEligible={isRightPaymentEligible}
                    key={vehicle.id}
                    vehicle={vehicle}
                    index={index}
                    numberedCards={!isLoggedIn}
                    eventTag="Profile"
                  />
                  {!isLoggedIn && index === 0 && (
                    <div className="lg:hidden flex">
                      <SaveMoreVehiclesCard
                        cardDetails={saveMoreCardDetails()}
                      />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
            {isLoggedIn && !isLoading && (
              <SaveMoreVehiclesCard cardDetails={saveMoreCardDetails()} />
            )}

            {!isLoggedIn && (
              <>
                {getEmptyCards()}
                <div className="hidden lg:flex">
                  <SaveMoreVehiclesCard cardDetails={saveMoreCardDetails()} />
                </div>
              </>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="w-full py-6 px-4 md:px-6 lg:px-0">
      <div className="pb-4">
        <div className="w-full flex items-center justify-between lg:justify-start">
          <h3>{t("common:saved_vehicles")}</h3>
          {onShowMenuClick && (
            <div>
              <BackButton onClick={() => onShowMenuClick()} />
            </div>
          )}
        </div>
      </div>
      {!isLoggedIn && (
        <div className="flex lg:hidden bg-primary-light">
          <div className="body-3 p-4 text-primary-bold md:w-3/4 h-1/2 sm:h-1/4">
            <p className="body-1 pb-2">
              {t("common:go_unlimited").toUpperCase()}
            </p>
            <p>{t("common:go_unlimited_description")}</p>
            <div className="m-auto md:hidden">
              <Button
                buttonStyle="transparent"
                fill="outline"
                size="small"
                onClick={handleLoginClicked}
                className="mt-[1rem] border-2"
              >
                {t("common:log_in_or_sign_up")}
              </Button>
            </div>
          </div>
          <div className="m-auto hidden md:flex">
            <Button
              buttonStyle="transparent"
              fill="outline"
              size="small"
              onClick={handleLoginClicked}
              className="border-2"
            >
              {t("common:log_in_or_sign_up")}
            </Button>
          </div>
        </div>
      )}
      {showEmptySavedVehicles ? (
        <EmptySavedVehicles />
      ) : (
        <InfiniteScroll
          scrollableTarget="scrollable-container"
          dataLength={user?.favouriteVehicles?.length || 0}
          next={fetchNextPage}
          hasMore={hasNextPage || false}
          loader={
            isFetchingNextPage && <LoadingSpinner isLoading isOverlay={false} />
          }
        >
          {isLoggedIn ? (
            <>
              {(user?.favouriteVehicles?.length || 0) > 0 && (
                <div className="body-3 text-text-light-200 pt-4">
                  {t("profile:vehicles_count", {
                    count: user?.favouriteVehicles.length,
                  })}
                </div>
              )}
              {vehiclesList()}
            </>
          ) : (
            <>
              {(user?.favouriteVehicles?.length || 0) > 0 && (
                <div className="body-3 text-text-light-200 pt-2 pb-3">
                  {t("profile:vehicles_count", {
                    count: user?.favouriteVehicles.length,
                  })}
                </div>
              )}

              {vehiclesList()}

              {favouriteVehiclesCopy !== undefined && (
                <div className="py-10">
                  <Link href={`/${VLP_PATH}`}>
                    <Button buttonStyle="primary" fill="filled">
                      {t("common:shop_all_inventory")}
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}
        </InfiniteScroll>
      )}
    </div>
  );
};

export default SavedVehicles;
